import { ChevronRight, Loader2, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import {
  Chip,
  PageHeader,
  PaginationFooter,
  ProvenanceBadge,
  StatusDot,
  type StatusDotStatus,
} from "@/components/comms";
import {
  type AttentionLevel,
  COMMS_PAGE_SIZE,
  EVENT_LABELS,
  type NextEventResult,
  useOrderingCustomers,
} from "@/lib/comms";
import { cn, formatShortDateTime } from "@/lib/utils";

import { DemoControls } from "./demo-controls";

interface EventBannerProps {
  event: NextEventResult;
  onDismiss: () => void;
  onView: () => void;
}

/**
 * What the agent just did, spelled out: which event, on which account and
 * order, where the order now sits in its lifecycle, who the mail went to (or
 * why it didn't), and when. Stays on screen until dismissed.
 */
function EventBanner({ event, onDismiss, onView }: EventBannerProps) {
  const emailed = event.outcome === "emailed";
  return (
    <div role="status" className="border-jj-red/30 bg-jj-red/5 rounded-xl border px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="bg-jj-red size-2 shrink-0 rounded-full" />
        <span className="text-wg-08">
          <b>{EVENT_LABELS[event.event_type]}</b> landed on <b>{event.account_name}</b>
        </span>
        <Chip className={emailed ? "border-ok/40 bg-ok/10 text-ok" : undefined}>
          {emailed ? "Email sent" : "No email — suppressed"}
        </Chip>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss this event"
          title="Dismiss"
          className="text-wg-06 hover:text-wg-08 ml-auto cursor-pointer"
        >
          <X className="size-4" />
        </button>
      </div>

      <dl className="text-wg-07 mt-2 grid gap-x-6 gap-y-1 text-[12.5px] sm:grid-cols-[auto_1fr]">
        <dt className="font-semibold">Order</dt>
        <dd className="tabular-nums">
          #{event.order_number}
          {event.step != null && event.total_steps != null
            ? ` · lifecycle step ${event.step} of ${event.total_steps}`
            : " · backorder — the exception lane, outside the six-step flow"}
        </dd>

        <dt className="font-semibold">{emailed ? "Emailed to" : "Would have gone to"}</dt>
        <dd>
          {event.to}
          {event.cc && <span className="text-wg-06"> · CC {event.cc} (sales rep)</span>}
        </dd>

        <dt className="font-semibold">{emailed ? "Recorded" : "Suppressed"}</dt>
        <dd>
          {emailed
            ? "Email record written to the audit log"
            : `${event.suppression_reason ?? "Notification preferences"} — suppression logged against the order`}
        </dd>

        <dt className="font-semibold">Processed</dt>
        <dd className="tabular-nums">{formatShortDateTime(event.processed_at)}</dd>
      </dl>

      <button
        type="button"
        onClick={onView}
        className="text-jj-red mt-2 cursor-pointer text-xs font-semibold hover:underline"
      >
        View customer →
      </button>
    </div>
  );
}

const LEGEND: { status: StatusDotStatus; label: string }[] = [
  { status: "ok", label: "All orders on track" },
  { status: "progress", label: "Orders in progress" },
  { status: "risk", label: "Needs attention — backorder or exception" },
];

/** Selected-chip styling, one per status dot color. */
const LEGEND_ACTIVE_CLASSES: Record<AttentionLevel, string> = {
  ok: "border-ok/40 bg-ok/10 text-ok",
  progress: "border-warn/40 bg-warn/10 text-warn",
  risk: "border-risk/40 bg-risk/10 text-risk",
};

/** What the coloured dot means, for empty-state copy. */
const ATTENTION_LABELS: Record<AttentionLevel, string> = {
  ok: "green",
  progress: "yellow",
  risk: "red",
};

/** Empty-state copy that reflects whichever of search/attention filter is active. */
function emptyStateMessage(search: string, attention: AttentionLevel | undefined): string {
  if (search && attention) {
    return `No ${ATTENTION_LABELS[attention]} accounts match "${search}".`;
  }
  if (attention) {
    return `No ${ATTENTION_LABELS[attention]} accounts.`;
  }
  if (search) {
    return `No customers match "${search}".`;
  }
  return "No customers yet.";
}

export function OrderingListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [attention, setAttention] = useState<AttentionLevel | undefined>(undefined);

  // Clicking the active chip clears the filter; clicking another swaps it —
  // only one status can be active at a time. Either way, back to page one.
  const toggleAttention = (status: AttentionLevel) => {
    setAttention((prev) => (prev === status ? undefined : status));
    setOffset(0);
  };

  // Debounce the search box so we don't query on every keystroke; a new term
  // always jumps back to the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const customersQuery = useOrderingCustomers({
    limit: COMMS_PAGE_SIZE,
    offset,
    q: debouncedSearch || undefined,
    attention,
  });
  // Processed demo events, newest first — one dismissible banner each, plus a
  // row spotlight on the newest so the operator sees where it landed without
  // knowing account ids. Deliberately never auto-dismissed: the presenter
  // reads each one at their own pace and closes it by hand.
  const [events, setEvents] = useState<{ id: number; event: NextEventResult }[]>([]);
  const nextId = useRef(0);

  const handleEvent = (result: NextEventResult) => {
    setEvents((prev) => [{ id: nextId.current++, event: result }, ...prev]);
  };
  const dismiss = (id: number) => setEvents((prev) => prev.filter((e) => e.id !== id));
  const spotlightId = events[0]?.event.account_id;

  const page = customersQuery.data;
  const customers = page?.items ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          backTo="/home"
          backLabel="Home"
          title="Ordering Event Proactive Comms"
          subtitle="Ordering events · regular customers"
        />
        <div className="pt-1">
          <DemoControls onEvent={handleEvent} />
        </div>
      </div>

      {events.length > 0 && (
        <div className="mb-4 flex flex-col gap-2">
          {events.map(({ id, event }) => (
            <EventBanner
              key={id}
              event={event}
              onDismiss={() => dismiss(id)}
              onView={() => navigate(`/ordering/${event.account_id}`)}
            />
          ))}
        </div>
      )}

      <div className="mb-3 flex items-center justify-between gap-4">
        <span className="text-wg-07 text-sm font-semibold">Customer list</span>
        <div className="border-wg-03 flex w-[260px] items-center gap-2 rounded-full border bg-white px-3.5 py-2">
          <Search className="text-wg-06 size-4 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer"
            autoComplete="off"
            className="placeholder:text-wg-05 w-full bg-transparent text-sm outline-none"
          />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-x-3 gap-y-2 text-[12.5px]">
        {LEGEND.map((l) => {
          const active = attention === l.status;
          return (
            <button
              key={l.status}
              type="button"
              onClick={() => toggleAttention(l.status)}
              aria-pressed={active}
              title={active ? "Clear filter" : `Show only ${ATTENTION_LABELS[l.status]} accounts`}
              className={cn(
                "inline-flex cursor-pointer items-center gap-2 rounded-full border px-2.5 py-1 transition-colors",
                active
                  ? LEGEND_ACTIVE_CLASSES[l.status]
                  : "text-wg-06 hover:bg-wg-01 hover:text-wg-08 border-transparent",
              )}
            >
              <StatusDot status={l.status} />
              {l.label}
            </button>
          );
        })}
      </div>

      <div className="border-wg-03 overflow-hidden rounded-2xl border bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-wg-03 text-wg-06 border-b text-[12px] font-semibold">
              <th className="px-5 py-3">Account</th>
              <th className="px-5 py-3">Orders in progress</th>
              <th className="px-5 py-3">Comms preference</th>
              <th className="w-12 px-5 py-3" />
            </tr>
          </thead>
          <tbody className={cn(customersQuery.isFetching && "opacity-60")}>
            {customersQuery.isLoading ? (
              <tr>
                <td colSpan={4} className="px-5 py-10">
                  <Loader2 className="text-wg-05 mx-auto size-5 animate-spin" />
                </td>
              </tr>
            ) : customersQuery.isError ? (
              <tr>
                <td colSpan={4} className="text-wg-06 px-5 py-10 text-center">
                  Couldn’t load customers — please try again.
                </td>
              </tr>
            ) : (page?.total ?? 0) === 0 ? (
              <tr>
                <td colSpan={4} className="text-wg-06 px-5 py-10 text-center">
                  {emptyStateMessage(debouncedSearch, attention)}
                </td>
              </tr>
            ) : (
              customers.map((c) => (
                <tr
                  key={c.id}
                  tabIndex={0}
                  onClick={() => navigate(`/ordering/${c.id}`)}
                  onKeyDown={(e) => {
                    // Only keys on the row itself — keep inner controls free
                    // to handle their own activation.
                    if (e.target !== e.currentTarget) {
                      return;
                    }
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/ordering/${c.id}`);
                    }
                  }}
                  className={cn(
                    "border-wg-02 cursor-pointer border-b transition-colors duration-500 last:border-0",
                    "focus-visible:outline-jj-red focus-visible:outline-2 focus-visible:-outline-offset-2",
                    spotlightId === c.id ? "bg-jj-red/5" : "hover:bg-wg-01",
                  )}
                >
                  <td className="px-5 py-3.5">
                    <span className="flex items-center gap-3">
                      <StatusDot status={c.attention} />
                      <span className="flex flex-col gap-0.5">
                        <ProvenanceBadge
                          provenance={c.provenance}
                          className="text-[8px] opacity-60"
                        />
                        <span className="text-wg-08 leading-tight font-medium">{c.name}</span>
                        {c.account_number && (
                          <span className="text-wg-05 text-[11px] leading-tight italic">
                            {c.account_number}
                          </span>
                        )}
                      </span>
                    </span>
                  </td>
                  <td className="px-5 py-3.5 tabular-nums">{c.orders_in_progress}</td>
                  <td className="px-5 py-3.5">
                    {/* Exclusion outranks the preference — a key/EDI account
                        is never messaged whatever its toggles say. */}
                    {c.excluded ? (
                      <Chip className="border-wg-08 bg-wg-08 text-white">Excluded</Chip>
                    ) : (
                      <Chip variant={c.opted_in ? "opted-in" : "opted-out"}>
                        {c.opted_in ? "Opted in" : "Opted out"}
                      </Chip>
                    )}
                  </td>
                  <td className="text-wg-05 px-5 py-3.5">
                    <ChevronRight className="ml-auto size-4" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {page && page.total > 0 && (
          <PaginationFooter
            offset={page.offset}
            limit={page.limit}
            total={page.total}
            disabled={customersQuery.isFetching}
            onPrev={() => setOffset((o) => Math.max(0, o - COMMS_PAGE_SIZE))}
            onNext={() => setOffset((o) => o + COMMS_PAGE_SIZE)}
          />
        )}
      </div>
    </div>
  );
}
